import { useState, useEffect } from 'react';
import { makeApiCall } from '../utils/api';
import { API_PATHS } from '../config/api-endpoints';

/**
 * Response interface for GetUserLdapGroups API
 */
export interface GetUserLdapGroupsResponse {
    ldapGroups: string[];
}

/**
 * Option format for LDAP groups in UI components
 */
export interface LdapGroupOption {
    label: string;
    value: string;
}

interface UseUserLdapGroupsReturn {
    ldapGroups: LdapGroupOption[];
    loading: boolean;
    error: string | null;
    refresh: () => Promise<void>;
}

/**
 * Custom hook to fetch user's LDAP groups
 * @param autoFetch - Whether to automatically fetch on mount (default: true)
 */
export const useUserLdapGroups = (autoFetch: boolean = true): UseUserLdapGroupsReturn => {
    const [ldapGroups, setLdapGroups] = useState<LdapGroupOption[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchLdapGroups = async () => {
        setLoading(true);
        setError(null);

        try {
            console.log('Fetching user LDAP groups...');
            
            const response: GetUserLdapGroupsResponse = await makeApiCall(
                API_PATHS.PROFILES.LDAP_GROUPS,
                'POST',
                {}
            );

            console.log('LDAP groups response:', response);

            // Transform API response to UI format
            // Use raw group name as both label and value to show actual DB key
            const transformedGroups: LdapGroupOption[] = response.ldapGroups.map(group => ({
                label: group,  // Use actual LDAP group key as label
                value: group
            }));

            // Sort alphabetically by label
            transformedGroups.sort((a, b) => a.label.localeCompare(b.label));

            setLdapGroups(transformedGroups);
        } catch (err: any) {
            console.error('Failed to fetch LDAP groups:', err);
            setError(err.message || 'Failed to load LDAP groups');
            
            // Fallback to empty array on error
            setLdapGroups([]);
        } finally {
            setLoading(false);
        }
    };

    const refresh = async () => {
        await fetchLdapGroups();
    };

    // Auto-fetch on mount if enabled
    useEffect(() => {
        if (autoFetch) {
            fetchLdapGroups();
        }
    }, []); // Only run once on mount

    return {
        ldapGroups,
        loading,
        error,
        refresh
    };
};

/**
 * Helper function to format LDAP group names for display
 * Converts kebab-case to Title Case
 * @param group - LDAP group name (e.g., "team-engineering")
 * @returns Formatted label (e.g., "Team Engineering")
 */
function formatLdapGroupLabel(group: string): string {
    return group
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}
